import React, { useCallback, useEffect, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { localeCookieName, localeLocalStorageKey, STORAGE_KEY_ALIASES } from '~/app/storageKeys';
import { localeSchema } from '~/app/storageSchemas';
import { useHasMounted } from '~/components/ClientOnly';

import { configs, getLocalizedCountryCodes, I18nContext, translations } from './i18n-context';

interface I18nProviderProps {
  children: React.ReactNode;
  urlPath?: string;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, urlPath = '/' }) => {
  const [storedLocale, setStoredLocale] = useLocalStorageState(localeLocalStorageKey, {
    defaultValue: 'de',
  });
  const hasMounted = useHasMounted();
  const validatedCurrentLocale = localeSchema.safeParse(storedLocale);
  const currentLocale: 'de' | 'en' | 'es' = hasMounted
    ? validatedCurrentLocale.success
      ? validatedCurrentLocale.data
      : 'de'
    : 'de';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // One-time compatibility migration from legacy locale key.
    // TODO: [storage-migration-cleanup] Remove legacy locale migration after rollout.
    const legacy = localStorage.getItem(STORAGE_KEY_ALIASES.localeLocalStorage[0]);
    const parsedLegacy = localeSchema.safeParse(legacy);
    if (parsedLegacy.success && !localStorage.getItem(localeLocalStorageKey)) {
      setStoredLocale(parsedLegacy.data);
    } else if (legacy && !parsedLegacy.success) {
      localStorage.removeItem(STORAGE_KEY_ALIASES.localeLocalStorage[0]);
    }
  }, [setStoredLocale]);

  useEffect(() => {
    try {
      const maxAge = 60 * 60 * 24 * 365;
      document.cookie = `${localeCookieName}=${storedLocale}; path=/; max-age=${maxAge}`;
      // Keep legacy cookie for compatibility during migration.
      // TODO: [storage-migration-cleanup] Stop writing legacy locale cookie when safe.
      document.cookie = `${STORAGE_KEY_ALIASES.localeCookie[0]}=${storedLocale}; path=/; max-age=${maxAge}`;
    } catch {
      // ignore cookie write errors (non-browser contexts)
    }
  }, [storedLocale]);

  const t = useCallback(
    (
      key: string,
      options?: {
        pluralCount?: number;
        placeholders?: { [key: string]: string | number };
      },
    ) => {
      const localeTranslations = translations[currentLocale as keyof typeof translations];
      let translation =
        localeTranslations && key in localeTranslations ? localeTranslations[key] : key;

      if (options?.pluralCount !== undefined) {
        if (
          options.pluralCount === 0 &&
          translations[currentLocale as keyof typeof translations]?.[`${key}_empty`]
        )
          translation = translations[currentLocale as keyof typeof translations]?.[`${key}_empty`];
        else if (
          options.pluralCount > 1 &&
          translations[currentLocale as keyof typeof translations]?.[`${key}_plural`]
        )
          translation = translations[currentLocale as keyof typeof translations]?.[`${key}_plural`];
      }

      options &&
        options.placeholders &&
        Object.entries(options.placeholders).forEach(([k, v]) => {
          translation = translation.replace(`{{${k}}}`, `${v}`);
        });
      return translation;
    },
    [currentLocale],
  );

  const localizedCountryCodes = useMemo(
    () => getLocalizedCountryCodes(currentLocale),
    [currentLocale],
  );

  const value = useMemo(
    () => ({
      t,
      config: configs[currentLocale as keyof typeof configs],
      locale: currentLocale,
      allLocales: [
        { locale: 'de', country: 'de' },
        { locale: 'en', country: 'en' },
        { locale: 'es', country: 'es' },
      ],
      country: configs[currentLocale as keyof typeof configs]?.country || 'de',
      localizedCountryCodes,
      allLocaleConfigs: configs,
      urlPath,
    }),
    [t, currentLocale, urlPath, localizedCountryCodes],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
